import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Users as UsersIcon } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import CreateSubscriberModal from '../components/CreateSubscriberModal';
import subscriberService, { Subscriber } from '../services/subscriberService';

const Assinantes: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState<Subscriber | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await subscriberService.getSubscribers();
      setSubscribers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar assinantes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedSubscriber(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEdit = (subscriber: Subscriber) => {
    setSelectedSubscriber(subscriber);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await subscriberService.deleteSubscriber(id);
      await loadSubscribers();
      setDeleteConfirm(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao excluir assinante');
    }
  };

  const handleSubmit = async (subscriberData: Partial<Subscriber>) => {
    if (modalMode === 'create') {
      await subscriberService.createSubscriber(subscriberData);
    } else if (selectedSubscriber?.id) {
      await subscriberService.updateSubscriber(selectedSubscriber.id, subscriberData);
    }
    await loadSubscribers();
  };

  const getTypeBadge = (type?: string | null) => {
    if (type === 'business') {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
          Pessoa Jurídica
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
        Pessoa Física
      </span>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar currentPath="/assinantes" />

      <div className="flex-1 ml-0 md:ml-64 pt-14 md:pt-0">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
              <div className="flex items-center gap-3">
                <UsersIcon size={32} className="text-gold-600" />
                <h1 className="text-2xl md:text-3xl font-bold text-brown-800">Assinantes</h1>
              </div>
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors shadow-md text-sm md:text-base"
              >
                <Plus size={20} />
                Criar Assinante
              </button>
            </div>
            <p className="text-gray-600">
              Gerencie os assinantes da sua organização. Cadastre, edite e visualize informações
              de pessoas físicas e jurídicas.
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500"></div>
              <p className="mt-4 text-gray-600">Carregando assinantes...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Lista de Assinantes */}
          {!loading && !error && subscribers.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <UsersIcon size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Nenhum assinante cadastrado
              </h3>
              <p className="text-gray-600 mb-4">
                Comece criando seu primeiro assinante
              </p>
              <button
                onClick={handleCreate}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors"
              >
                <Plus size={20} />
                Criar Primeiro Assinante
              </button>
            </div>
          )}

          {!loading && !error && subscribers.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Telefone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscribers.map((subscriber) => (
                      <tr key={subscriber.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{subscriber.name}</div>
                          {subscriber.type === 'business' && subscriber.business_name && (
                            <div className="text-sm text-gray-500">{subscriber.business_name}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getTypeBadge(subscriber.type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {subscriber.email || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {subscriber.phone || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {subscriber.type === 'business'
                            ? subscriber.business_identification || '-'
                            : subscriber.personal_identification || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(subscriber)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit size={18} />
                            </button>
                            {deleteConfirm === subscriber.id ? (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => subscriber.id && handleDelete(subscriber.id)}
                                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                >
                                  Confirmar
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(subscriber.id || null)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Excluir"
                              >
                                <Trash2 size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <CreateSubscriberModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        subscriber={selectedSubscriber}
        mode={modalMode}
      />
    </div>
  );
};

export default Assinantes;
